/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, colors, Grid, HFlow, Icon, InfoLabel, Text, Theme, Tooltip, useStyles, VFlow } from 'bold-ui'
import { AccordionButton } from 'components/accordion/AccordionButton'
import { Box } from 'components/Box'
import { DateTime } from 'components/date'
import { AccordionField } from 'components/form/field/AccordionField'
import { HLabel } from 'components/HLabel'
import { CSSProperties, useState } from 'react'
import { AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'

import { ContentCardSection } from '../detail/soap/aside/components/ContentCardSection'
import { ExcluirAprazamentoModal } from './ExcluirAprazamentoModal'

// dates as strings just for now
export interface RegistroDoseProps {
  dataRegistro: string
  dataAplicacao: string
  dataValidadeLote: string
  estrategia: string
  nomeProfissional: string
  cbo: string
  dataAprazamento: string
  lote: string
  nomeFabricante: string
  viaDeAdministracao: string
  localDaAplicacao: string
  observacoes: string
  isGestante: boolean
  isPuerpera: boolean
  isViajante: boolean
  unidadeSaude: string
  openedAccordions?: (string | number)[]
}

export const RegistroDose = (props: RegistroDoseProps) => {
  const {
    dataRegistro,
    dataAplicacao,
    estrategia,
    nomeProfissional,
    cbo,
    lote,
    nomeFabricante,
    viaDeAdministracao,
    localDaAplicacao,
    observacoes,
    dataValidadeLote,
    isGestante,
    isPuerpera,
    isViajante,
  } = props

  const { classes, theme } = useStyles(createStyles)

  const buttonStyle = css`
    display: flex;
    padding: 0.5rem;
    border: 0.063rem solid ${theme.pallete.gray.c60};
    border-block-end: 0.063rem solid ${theme.pallete.gray.c80};
  `

  const cidadaoPossuiCondicao = isGestante || isPuerpera || isViajante
  const gestanteOuPuerperaViajante = (isGestante && isViajante) || (isPuerpera && isViajante)

  return (
    <VFlow vSpacing={0}>
      <HFlow hSpacing={0.5}>
        <Icon color={colors.blue.c50} icon='calendarFilled' size={1.0} />
        <Text fontWeight='bold'>Registro em </Text>
        <DateTime
          value={dataRegistro}
          style={css`
            font-weight: bold;
          `}
          format='DD/MM/DD/MM/YYYY [às] HH:mm'
        />
      </HFlow>
      <AccordionField name='Registro' allowZeroExpanded>
        <AccordionItem>
          <AccordionButton style={buttonStyle}>
            <HFlow>
              <Grid
                gap={0}
                style={css`
                  padding: 0.5rem;
                  width: 35rem;
                `}
              >
                <Cell size={1}>
                  <Icon icon='angleDown' size={1.5} />
                </Cell>
                <Cell size={6}>
                  <HFlow hSpacing={0.3}>
                    <Text fontWeight='bold'>Dose aplicada em </Text>
                    <DateTime
                      value={dataAplicacao}
                      style={css`
                        font-weight: bold;
                      `}
                      format='DD/MM/YYYY'
                    />
                  </HFlow>
                  <HLabel title='Estratégia'>{estrategia?.capitalize()}</HLabel>
                </Cell>
                <Cell size={5}>
                  <InfoLabel title={nomeProfissional}>{cbo}</InfoLabel>
                </Cell>
              </Grid>
            </HFlow>
          </AccordionButton>
          <AccordionItemPanel className={classes.panel}>
            <Grid
              gap={0}
              gapVertical={1}
              style={css`
                padding: 1rem;
                width: 36rem;
              `}
            >
              <Cell size={7}>
                <InfoLabel title='Lote/Fabricante'>{`${lote?.capitalize()}/${nomeFabricante?.capitalize()}`}</InfoLabel>
              </Cell>
              <Cell size={5}>
                <InfoLabel title='Data de validade'>
                  <DateTime value={dataValidadeLote} format='DD/MM/YYYY' />
                </InfoLabel>
              </Cell>
              <Cell size={7}>
                <InfoLabel placeholder='-' title='Via de administração'>
                  {viaDeAdministracao?.capitalize()}
                </InfoLabel>
              </Cell>
              <Cell size={4}>
                <InfoLabel placeholder='-' title='Local da aplicação'>
                  {localDaAplicacao?.capitalize()}
                </InfoLabel>
              </Cell>
              <Cell size={12}>
                <InfoLabel title='Observações' placeholder='-'>
                  {observacoes}
                </InfoLabel>
              </Cell>
              {cidadaoPossuiCondicao && (
                <Cell size={12}>
                  <HFlow hSpacing={0.5}>
                    <Icon icon='infoCircleFilled' size={1} fill='primary' />
                    <Text fontStyle='italic'>
                      {isGestante && 'Gestante'} {isPuerpera && 'Puérpera'} {gestanteOuPuerperaViajante && ' e '}
                      {isViajante && 'Viajante '}
                    </Text>
                  </HFlow>
                </Cell>
              )}
            </Grid>
          </AccordionItemPanel>
        </AccordionItem>
      </AccordionField>
    </VFlow>
  )
}

export const AprazamentoDose = (props: RegistroDoseProps) => {
  const { dataRegistro, dataAprazamento, estrategia, nomeProfissional, cbo } = props
  const [isModalExcluirAprazamentoOpen, setIsModalExcluirAprazamentoOpen] = useState(false)

  return (
    <VFlow vSpacing={0}>
      <ContentCardSection>
        <HFlow hSpacing={0.25}>
          <Icon color={colors.blue.c50} icon='calendarFilled' size={1.0} />
          <Text fontWeight='bold'>Registro em </Text>
          <DateTime
            value={dataRegistro}
            style={css`
              font-weight: bold;
            `}
            format='DD/MM/YYYY [às] HH:mm'
          />
        </HFlow>
      </ContentCardSection>
      <Box>
        <Grid gap={0} gapVertical={1}>
          <Cell size={6}>
            <HFlow hSpacing={0.3}>
              <Text fontWeight='bold'>Dose aprazada para </Text>
              <DateTime
                value={dataAprazamento}
                style={css`
                  font-weight: bold;
                `}
                format='DD/MM/YYYY'
              />
            </HFlow>
            <HLabel title='Estratégia'>{estrategia?.capitalize()}</HLabel>
          </Cell>
          <Cell size={4}>
            <InfoLabel title={nomeProfissional}>{cbo}</InfoLabel>
          </Cell>
          <Cell size={2}>
            <HFlow hSpacing={0}>
              <Tooltip text='Editar'>
                <Button kind='normal' onClick={function noRefCheck() {}} size='small' skin='ghost'>
                  <Icon icon='penOutline' />
                </Button>
              </Tooltip>
              <Tooltip text='Excluir'>
                <Button kind='danger' onClick={() => setIsModalExcluirAprazamentoOpen(true)} size='small' skin='ghost'>
                  <Icon icon='trashOutline' />
                </Button>
              </Tooltip>
            </HFlow>
          </Cell>
        </Grid>
      </Box>
      <ExcluirAprazamentoModal
        modalOpen={isModalExcluirAprazamentoOpen}
        handleModalClose={() => setIsModalExcluirAprazamentoOpen(false)}
      ></ExcluirAprazamentoModal>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  panel: {
    margin: '0 0.3rem',
    borderWidth: '0 0.063rem 0.063rem',
    borderStyle: 'solid',
    borderColor: theme.pallete.gray.c60,
    position: 'relative',
    top: '-0.6rem',
  } as CSSProperties,
})
